import {Button, Col, Container, Form, Row} from 'react-bootstrap';
import {useEffect, useRef, useState} from 'react';
import {FormattedMessage, IntlShape} from 'react-intl';
import PropTypes from 'prop-types';
import log from 'loglevel';
import _ from 'lodash'
import * as yup from 'yup';

import {api} from '../api';
import CallForPapersMailtoLink from './CallForPapersMailtoLink';
import CallForPapersFormWidget, {
    baseSchema,
    initialValue,
    submissionValue,
    widgetSchema
} from './CallForPapersFormWidget';
import {Formik, FormikProps, useFormikContext} from 'formik';
import {useHistory, useParams, withRouter} from 'react-router-dom';
import TaskContainer from './Layout/TaskContainer';
import {
    CallForPaperForm,
    CallForPapers,
    FormWidget,
    PaperSubmission,
    PaperSubmissionFormDatum,
    ReactRouterParamTypes,
    TaskProps,
} from '../types/types';
import MyAreaButton from './MyArea/MyAreaButton';

interface CallForPapersFormProps {
    intl: IntlShape;
    callForPapers: CallForPapers;
    roles: any;
    submission?: PaperSubmission;
}

interface CallForPapersFormFields {
    widgets?: FormWidget[];
    tasks?: TaskProps[];
}

// Initialwerte einer Einreichung
const DefaultCallForPapersFormInitialValues: CallForPapersFormFields = {
    tasks: []
}

/**
 * Formular basierend auf einem CallForPaper über welches Einreichungen für eine Veranstaltung getätigt werden.
 * Rendert neben einiger zwingend notwendiger Tickets beliebig viele optionale Widgets.
 *
 * @constructor
 */
const CallForPapersForm = ({intl, callForPapers, roles}: CallForPapersFormProps) => {
    const history = useHistory();
    const schema = createValidationSchema(callForPapers.form);

    const {submissionId} = useParams<ReactRouterParamTypes>();
    const [submission, setSubmission] = useState<PaperSubmission | undefined>(undefined);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isValidForm, setIsValidForm] = useState(false);
    const [taskCount, setTaskCount] = useState(0);
    const [hasSubmissionBeenPlanned, setHasSubmissionBeenPlanned] = useState(false);

    const initialValues = createInitialValues(callForPapers, submission);
    const isEdit = !!submission;

    // Referenz auf das Formular, wird für custom Validierung außerhalb von Formik benötigt
    let formRef: any = useRef<FormikProps<any>>(null);

    useEffect(() => {
        const loadPaperSubmission = async () => {
            setSubmission(submissionId ? await api.fetchPaperSubmission(submissionId) : undefined);
        }

        const loadTaskCountForUser = async () => {
            return await api.fetchTaskCountForUser();
        }

        const hasSubmissionBeenPlanned = async () => {
            setHasSubmissionBeenPlanned(submissionId ? await api.fetchHasSubmissionBeenPlanned(submissionId) : false);
        }

        loadPaperSubmission();
        loadTaskCountForUser().then(res => {
            setTaskCount(res);
        });
        hasSubmissionBeenPlanned();
    }, [submissionId]);

    /**
     * Submit-Handler, welcher an Formiks handleChange-prop übergeben wird
     *
     * @param values
     */
    const handleSubmit: (values: CallForPapersFormFields) => void = (values: CallForPapersFormFields) => {
        const widgetFormData: { id: string, value: any, key: string }[] = _.map(callForPapers.form.widgets, (widget: FormWidget) => {
            let value: any = _.get(values, `widgets[${widget.id}]`);

            // String-Werte trimmen und leere Strings als NULL bzw. als nicht ausgefüllt werten.
            if (_.isString(value)) {
                value = _.trim(value);
                value = value ? value : null;
            }

            return {id: widget.id, value: value, key: widget.backendLabel};
        });

        const submissionData = {
            ...values,
            language: intl.locale,
            widgetData: widgetFormData
        };

        log.debug('submit:', submissionData);
        setIsSubmitting(true);

        if (submission && submission?.id) {
            api.updatePaperSubmission(submission.id, submissionData)
                .then(res => {
                    log.debug('paper updated!', res);
                    history.push(`/${callForPapers.urlSlug}/result`);
                })
                .catch(() => {
                    setIsSubmitting(false);
                });
        } else {
            api.submitPaper(callForPapers.urlSlug, submissionData)
                .then(res => {
                    log.debug('paper submitted!', res);
                    history.push(`/${callForPapers.urlSlug}/result`);
                })
                .catch(() => {
                    setIsSubmitting(false);
                });
        }
    };

    /**
     * Gibt zurück, ob der Inhalt der Einreichung bearbeitet werden kann.
     *
     * @return {boolean}
     */
    const isEditable: () => boolean = () => {
        return !(isAfterDeadline() || submissionDeclined() || hasSubmissionBeenPlanned);
    }

    /**
     * Prüft ob die Bearbeitungsdeadline erreicht wurde
     *
     * @return {boolean}
     */
    const isAfterDeadline: () => boolean = () => {
        return new Date().getTime() > new Date(callForPapers.editDeadline).getTime();
    };

    /**
     * Prüft ob eine Einreichung abgelehnt wurde
     *
     * @return {boolean}
     */
    const submissionDeclined: () => boolean = () => {
        if (submission && submission.decision) {
            return submission.decision === 'SUBMISSION_DECLINED';
        }

        return false;
    };

    /**
     * Prüft ob alle per C4P-Definition required custom form elements auch wirklich befüllt sind
     */
    const validateRequiredWidgets: () => void = () => {
        const isValid: (widget: FormWidget, value: any) => boolean = (widget: any, value: any) => {
            // wenn das custom widget unbekannt ist (z.B. wenn es dekorativ ist) oder wenn das optionale widget nicht
            // required ist, ist die Eingabe immer "valide"
            if ((!widget.baseWidget && !widget.required) || value === undefined) {
                return true;
            }

            if (_.isArray(value)) {
                return value.length !== 0;
            }
            return value !== '' && value !== null;
        }

        /**
         * Prüft ob alle Pflichtfelder des Formulars ausgefüllt sind und gibt somit an, ob das Formular valide ist.
         * Die restliche Validierung geschieht durch Formik und das YUP-Schema.
         */
        const formIsValid: boolean = _.every(callForPapers.form.widgets, (widget: FormWidget) => {
            let value: any = _.get(formRef, `values.widgets[${widget.id}]`, undefined);
            return isValid(widget, value);
        });

        setIsValidForm(formIsValid);
    };

    /**
     * Gibt einen Hinweis zurück, dass eine Einreichung nicht mehr bearbeitbar ist, samt mailto-Link
     */
    const getEditDisabledHint: () => JSX.Element | null = () => {
        if (isAfterDeadline()) {
            return (<p>
                <CallForPapersMailtoLink intlId="submission.form.header.edit.deadline.past" intl={intl}
                                         submission={submission}/>
            </p>);
        }

        if (hasSubmissionBeenPlanned) {
            return (<p>
                <CallForPapersMailtoLink intlId="submission.form.header.edit.planned" intl={intl}
                                         submission={submission}/>
            </p>);
        }

        return null;
    };

    /**
     * Rendert alle Widgets des Formulars
     *
     * @param formikProps
     */
    const renderWidgets: (formikProps: FormikProps<any>) => JSX.Element[] = (formikProps: FormikProps<any>) => {
        return _.get(callForPapers, 'form.widgets', [])
            .filter((widget: any) => widget.active) // Nur aktive Widgets sollen angezeigt werden
            .map((widget: any) => <CallForPapersFormWidget
                key={widget.id}
                widget={widget}
                disabled={!isEditable()}
                formikProps={formikProps}
                callForPapers={callForPapers}
                submission={submission}
                roles={roles}/>
            );
    };

    /**
     * Rendert den Submit-Button anhand des aktuellen Zustandes der Einreichung und des C4Ps
     *
     * @returns {JSX.Element}
     */
    const renderSubmitButton: () => JSX.Element | null = () => {
        if (callForPapers.isPublishedVersion) {
            // Wenn Einreichungs-Entwürfe erlaubt sind im Falle eines unvollständigen Formulars save statt submit
            let buttonLabelId: string = callForPapers.form.allowPartialSubmissions && !isValidForm ? 'save' : 'submit';
            if (isAfterDeadline()) {
                buttonLabelId = 'deadline';
            }
            if (submissionDeclined()) {
                buttonLabelId = 'declined';
            }
            if (hasSubmissionBeenPlanned) {
                buttonLabelId = 'planned';
            }

            let editDisabledHint: JSX.Element | null = getEditDisabledHint();

            return (
                <>
                    <Button variant="primary" type="submit" disabled={!isEditable() || isSubmitting}>
                        <FormattedMessage id={`submission.form.button.${buttonLabelId}`}/>
                    </Button>

                    {editDisabledHint &&
                    <>
                        <hr/>
                        {editDisabledHint}
                    </>
                    }
                </>
            );
        }

        return null;
    };

    /**
     * Rendert die Überschrift für wenn eine Einreichung existiert und man somit im /edit-mode ist
     *
     * @returns {JSX.Element}
     */
    const renderEditHeader: () => JSX.Element | null = () => {
        let editabilityHint: JSX.Element | null = getEditDisabledHint();
        if (isEditable()) {
            editabilityHint = (<p>
                <FormattedMessage id="submission.form.header.edit.deadline"/>
                <em>{new Date(callForPapers.editDeadline).toLocaleDateString()}</em>
            </p>);
        }

        if (isEdit) {
            return (
                <div>
                    <h2>
                        <FormattedMessage id="submission.form.header.edit.mode"/>
                        <em>{callForPapers.versionContainer.event.name}</em>
                    </h2>
                    {editabilityHint}
                    <hr/>
                </div>
            );
        }

        return null;
    };

    // Haupt-Render der Functional Component
    return (
        <Formik
            enableReinitialize={true}
            innerRef={ref => (formRef = ref)} /* ref setzen und formik bag zu erhalten für die aktuellen Werte */
            initialValues={initialValues}
            validationSchema={schema}
            onSubmit={handleSubmit}
        >
            {
                formikProps => (
                    <Container>
                        <OnChangeHandlerComponent validateFunc={validateRequiredWidgets}/>
                        <TaskContainer
                            formikProps={formikProps}
                            submission={submission}
                            callForPapers={callForPapers}
                            isValidForm={isValidForm}
                        />

                        <MyAreaButton taskCount={taskCount}/>
                        {renderEditHeader()}
                        <Row>
                            <Col>
                                <Form
                                    noValidate
                                    onSubmit={formikProps.handleSubmit}
                                    onChange={() => validateRequiredWidgets()}
                                >
                                    {renderWidgets(formikProps)}
                                    {renderSubmitButton()}
                                </Form>
                            </Col>
                        </Row>
                    </Container>
                )
            }
        </Formik>
    );
};

interface OnChangeHandlerComponentProps {
    validateFunc: () => void
}

/**
 * Formik besitzt Stand 06.2021 keine modufizierbare onChange Logik, wodurch keine Logiken von außen hinzugefügt
 * werden können. Um die Custom-Validierung, welche im Zuge von W2-33 eingeführt wurde, immer dann auszuführen,
 * wenn sich der Formik-State ändert, wird folgende Komponente benötigt, die innerhalb des Formik-Kontextes
 * useEffect nutzt, sobald die values des FormikContext verändert werden.
 * Andernfalls findet eine Validierung, die auf change der Referenz des Formulars horch, zu spät statt bzw. die formRef
 * ist "veraltet" (wird ausgeführt, bevor Wert tatsächlich übernommen)
 * Siehe https://github.com/formium/formik/issues/1633#issuecomment-630870985
 *
 * @param validateFunc
 * @constructor
 */
const OnChangeHandlerComponent = ({validateFunc}: OnChangeHandlerComponentProps) => {
    const {values} = useFormikContext();

    useEffect(() => {
        validateFunc();
    }, [values, validateFunc]);

    return null;
};

/**
 * Erzeugt die Initialen Werte der Formularfelder. Hierbei wird differenziert zwischen einer neuen Einreichung, bei der
 * alle Felder leer initialisiert werden, oder einer bestehenden Einreichung, bei der die alten Werte übernommen werden.
 *
 * @param callForPapers
 * @param submission
 */
const createInitialValues: (callForPapers: CallForPapers, submission?: PaperSubmission) => CallForPapersFormFields = (callForPapers: CallForPapers, submission?: PaperSubmission) => {
    const form: CallForPaperForm = _.get(callForPapers, 'form', {id: '', allowPartialSubmissions: true, widgets: []});

    const urlParams = new URLSearchParams(window.location.search);
    const widgetValues: FormWidget[] = _.reduce(form.widgets, (dataShape: any, widget: FormWidget) => {
        dataShape[widget.id] = initialValue(widget, urlParams);
        return dataShape;
    }, {} as FormWidget);

    // Wenn eine Einreichung gegeben ist, Daten initialisieren!
    if (submission) {
        const filledWidgetValues: FormWidget[] = _.reduce(submission.formData, (dataShape: any, widget: PaperSubmissionFormDatum) => {
            dataShape[widget.id] = widget.value;
            return dataShape;
        }, {} as PaperSubmissionFormDatum);

        const submissionValues: any = _.transform(callForPapers.form.widgets, (dataShape: any, widget: FormWidget) => {
            const value: any = submissionValue(callForPapers, widget, submission)
            if (value !== undefined) {
                dataShape[widget.id] = value
            }

        }, {} as any)

        return {
            widgets: {
                ...submissionValues,
                ...filledWidgetValues
            },
            tasks: submission.tasks ?? []
        };
    }

    DefaultCallForPapersFormInitialValues.widgets = widgetValues;
    return DefaultCallForPapersFormInitialValues
};

/**
 * Erzeugt das Validierungsschema für Formik anhand der Form-Widget-Defintion des CallForPapers
 *
 * @param form
 */
const createValidationSchema = (form: CallForPaperForm) => {
    const widgetSchemaConfiguration: yup.ObjectSchemaDefinition<FormWidget> = _.reduce(
        form.widgets,
        (dataShape: any, widget: FormWidget) => {
            dataShape[widget.id] = widgetSchema(widget, form.allowPartialSubmissions);
            return dataShape;
        },
        {}
    );

    let baseSchemaConfiguration = {
        widgets: yup.object().shape(widgetSchemaConfiguration).nullable()
    };

    baseSchemaConfiguration = _.reduce(form.widgets, (schema: any, widget: FormWidget) => {
        Object.assign(schema, baseSchema(widget));
        return schema;
    }, baseSchemaConfiguration);

    return yup.object().shape(baseSchemaConfiguration);
};

CallForPapersForm.propTypes = {
    callForPapers: PropTypes.object
};

// FIXME typehinting ordentlich...
// @ts-ignore
export default withRouter(CallForPapersForm);
